import React, { useEffect } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { todayISO, validateDateRange } from "../../Helpers/Utils/dateRange";

interface DateRangeFilterProps {
  /** Current `YYYY-MM-DD` values (empty string / undefined means "not set"). */
  startDate?: string;
  endDate?: string;
  /**
   * Called when either date changes. Signature matches the existing
   * `onFilterChange(filterName, value)` used throughout the SlidingFilter drawers.
   * `key` is intentionally `any` so drawer handlers typed `(key: keyof XFilters, ...)`
   * can be passed directly without a wrapper.
   */
  onChange: (key: any, value: string) => void;
  /** Filter keys the parent stores these under (defaults match most drawers). */
  startKey?: string;
  endKey?: string;
  /** Optional section heading shown above the row. */
  label?: string;
  fromLabel?: string;
  toLabel?: string;
  /** Notifies the parent whether the current range is valid, so it can gate "Apply". */
  onValidityChange?: (valid: boolean) => void;
  size?: "small" | "medium";
}

/**
 * Reusable From/To date-range filter with built-in validation:
 *  - future dates are blocked (max = today),
 *  - From ≤ To is enforced via cross-linked min/max,
 *  - an inline validation message is shown for invalid selections.
 *
 * FILTERS ONLY — do not use for form date fields where future dates are valid.
 */
const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  startDate,
  endDate,
  onChange,
  startKey = "startDate",
  endKey = "endDate",
  label = "Date Range",
  fromLabel = "From Date",
  toLabel = "To Date",
  onValidityChange,
  size = "small",
}) => {
  const today = todayISO();
  const { valid, message } = validateDateRange(startDate, endDate);

  // From cannot be after today, nor after the chosen To date.
  const fromMax = endDate && endDate < today ? endDate : today;
  // To cannot be before the chosen From date, nor after today.
  const toMin = startDate || undefined;

  useEffect(() => {
    onValidityChange?.(valid);
    // Only react to validity flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid]);

  return (
    <Box>
      {label ? (
        <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
          {label}
        </Typography>
      ) : null}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <TextField
          size={size}
          label={fromLabel}
          type="date"
          value={startDate || ""}
          onChange={(e) => onChange(startKey, e.target.value)}
          error={!valid}
          inputProps={{ max: fromMax }}
          sx={{ flex: 1 }}
          InputLabelProps={{ shrink: true }}
        />
        <Typography variant="body2" sx={{ mx: 1 }}>
          -
        </Typography>
        <TextField
          size={size}
          label={toLabel}
          type="date"
          value={endDate || ""}
          onChange={(e) => onChange(endKey, e.target.value)}
          error={!valid}
          inputProps={{ min: toMin, max: today }}
          sx={{ flex: 1 }}
          InputLabelProps={{ shrink: true }}
        />
      </Box>
      {!valid && message ? (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
          {message}
        </Typography>
      ) : null}
    </Box>
  );
};

export default DateRangeFilter;
